/* ============================================================================
   geske-about.js
   Motion controller for the About page (geske.com/int/about-us parity).
   Loaded only on template.suffix == 'about', after GSAP (+ScrollTrigger,
   +Draggable) and Embla. Each section registers an initializer via
   GxAbout.inits.push(fn); they all run once on DOMContentLoaded.
   Everything degrades gracefully: if a lib is missing, the section keeps its
   final CSS state (no carousel/scrub, but fully readable).
   ============================================================================ */
(function () {
  'use strict';

  var ROOT = document.querySelector('.template-page-about');
  if (!ROOT) return;

  var GX = (window.GxAbout = {
    inits: [],
    DESKTOP: '(min-width: 1024px)',
    TABLET_DOWN: '(max-width: 1023px)'
  });

  /* true viewport height (mobile URL-bar safe), mirrors .com --real-height */
  function setVH() {
    ROOT.style.setProperty('--gx-real-height', window.innerHeight + 'px');
  }
  setVH();
  window.addEventListener('resize', setVH);

  GX.isDesktop = function () { return window.matchMedia(GX.DESKTOP).matches; };

  /* InView reveal: add .is-inview when [data-gx-reveal] enters viewport */
  GX.initReveal = function () {
    var els = ROOT.querySelectorAll('[data-gx-reveal]');
    if (!els.length) return;
    if (!('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-inview'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          var el = e.target;
          var delay = el.getAttribute('data-gx-delay');
          if (delay) el.style.transitionDelay = delay + 'ms';
          el.classList.add('is-inview');
          io.unobserve(el);
        }
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -8% 0px' });
    els.forEach(function (el) { io.observe(el); });
  };

  /* Embla helper — returns the api or null. opts merged over sensible defaults */
  GX.embla = function (node, opts, plugins) {
    if (!node || typeof window.EmblaCarousel !== 'function') return null;
    var base = { align: 'start', containScroll: 'trimSnaps', dragFree: false, loop: false };
    for (var k in (opts || {})) base[k] = opts[k];
    try { return window.EmblaCarousel(node, base, plugins || []); }
    catch (e) { return null; }
  };

  /* wire prev/next/pager around an embla api */
  GX.wireCarousel = function (api, els) {
    if (!api) return;
    function update() {
      var i = api.selectedScrollSnap();
      if (els.current) els.current.textContent = i + 1;
      if (els.prev) els.prev.disabled = !api.canScrollPrev();
      if (els.next) els.next.disabled = !api.canScrollNext();
    }
    if (els.prev) els.prev.addEventListener('click', function () { api.scrollPrev(); });
    if (els.next) els.next.addEventListener('click', function () { api.scrollNext(); });
    api.on('select', update);
    api.on('reInit', update);
    update();
  };

  var gsapOK = !!window.gsap;
  if (gsapOK) {
    try {
      if (window.ScrollTrigger) window.gsap.registerPlugin(window.ScrollTrigger);
      if (window.Draggable) window.gsap.registerPlugin(window.Draggable);
    } catch (e) { gsapOK = false; }
  }
  GX.gsapOK = gsapOK;

  /* --- 1. Hero scroll cue ---------------------------------------------------- */
  GX.inits.push(function () {
    var btn = ROOT.querySelector('[data-gx-scroll]');
    if (!btn) return;
    btn.addEventListener('click', function () {
      var hero = btn.closest('.section-hero');
      var h = hero ? hero.getBoundingClientRect().height : window.innerHeight;
      window.scrollTo({ top: (window.pageYOffset || 0) + h - 64, behavior: 'smooth' });
    });
  });

  /* --- 3. Sticky scroll cross-fade (SectionSticky) --------------------------- */
  GX.inits.push(function () {
    var root = ROOT.querySelector('[data-gx-sticky]');
    if (!root) return;
    var track = root.querySelector('[data-gx-sticky-track]');
    var slides = Array.prototype.slice.call(root.querySelectorAll('.section-sticky__section'));
    var n = slides.length;
    if (!n || !track) return;
    var mq = window.matchMedia('(min-width: 1024px)');

    function docTop(el) { return el.getBoundingClientRect().top + window.pageYOffset; }

    function layout() {
      if (mq.matches) {
        track.style.height = (window.innerHeight * (n + 1)) + 'px';
      } else {
        track.style.height = 'auto';
        root.classList.remove('enabled');
        slides.forEach(function (s) { s.classList.add('active'); });
      }
    }

    function onScroll() {
      if (!mq.matches) return;
      var top = docTop(root);
      var h = root.offsetHeight;
      var sy = window.pageYOffset;
      var vh = window.innerHeight;
      var sticky = sy > top && sy < top + h;
      var idx = 0;
      if (sy > top + h - vh) { idx = n - 1; sticky = false; }
      else if (sticky) { idx = Math.floor(Math.abs((top - sy) / vh)); }
      idx = Math.max(0, Math.min(n - 1, idx));
      root.classList.toggle('enabled', sticky);
      slides.forEach(function (s, i) {
        s.classList.toggle('active', i === idx);
        if (i === 0) s.classList.toggle('visible', idx === 0);
      });
    }

    layout();
    onScroll();
    window.addEventListener('resize', function () { layout(); onScroll(); });
    window.addEventListener('scroll', onScroll, { passive: true });
    if (mq.addEventListener) mq.addEventListener('change', function () { layout(); onScroll(); });
  });

  /* --- 4. Text-images scroll-scrub scale (SectionTextImages) ----------------- */
  GX.inits.push(function () {
    if (!GX.gsapOK || !window.ScrollTrigger) return;
    var cover = ROOT.querySelector('[data-gx-ti-cover]');
    var title = ROOT.querySelector('[data-gx-ti-title]');
    if (!cover && !title) return;
    var mm = window.gsap.matchMedia();
    mm.add('(min-width: 1024px)', function () {
      if (cover) {
        window.gsap.fromTo(cover, { scale: 0.83 }, {
          scale: 1, ease: 'none',
          scrollTrigger: { trigger: cover, start: 'top 95%', end: 'center center', scrub: 0.5 }
        });
      }
      if (title) {
        window.gsap.fromTo(title, { scale: 1.32 }, {
          scale: 1, ease: 'none',
          scrollTrigger: { trigger: title, start: 'bottom 95%', end: 'bottom top', scrub: 0.5 }
        });
      }
    });
  });

  /* --- 5. Explore More mobile carousel (ExploreMore) ------------------------- */
  GX.inits.push(function () {
    var root = ROOT.querySelector('.explore-more');
    if (!root) return;
    var node = root.querySelector('[data-gx-embla]');
    if (!node) return;

    var api = null;
    var mq = window.matchMedia('(min-width: 1024px)');

    function destroy() {
      if (api) { try { api.destroy(); } catch (e) {} api = null; }
    }

    function build() {
      // Desktop: static wrapped flex grid, no carousel (transform none in CSS).
      if (mq.matches) { destroy(); return; }
      if (api) return;
      // align:'center' + containScroll:false used to park the first card ~62px
      // in from the container edge on phones, so it no longer lined up with the
      // heading above it and a half-cut slice of card 2 was chopped off at the
      // right edge - reported as the carousel "cutting off". Starting the track
      // at the container edge and trimming the end snaps keeps card 1 flush with
      // the section and turns the overhang into a deliberate peek of the next
      // card, with the last snap ending flush on the right.
      api = GX.embla(node, {
        align: 'start',
        containScroll: 'trimSnaps',
        slidesToScroll: 'auto'
      });
      if (!api) return; // Embla missing: list degrades to a swipe-free flex row.
      GX.wireCarousel(api, {
        prev: root.querySelector('[data-gx-prev]'),
        next: root.querySelector('[data-gx-next]'),
        current: root.querySelector('[data-gx-current]')
      });
    }

    build();
    if (mq.addEventListener) {
      mq.addEventListener('change', build);
    } else if (mq.addListener) {
      mq.addListener(build);
    }
  });

  /* --- 5. Team image slider (ImageSlider) ------------------------------------ */
  GX.inits.push(function () {
    var root = ROOT.querySelector('[data-gx-image-slider]');
    if (!root) return;
    var node = root.querySelector('[data-gx-image-slider-viewport]');
    if (!node) return;

    // Embla runs at every breakpoint here (the .com slider is always a carousel).
    var api = GX.embla(node, {
      align: 'start',
      containScroll: 'trimSnaps',
      slidesToScroll: 1,
      duration: 20
    });
    // No Embla: the slides stay a readable, swipeable overflow row (see CSS).
    if (!api) return;

    root.classList.add('is-embla');
    GX.wireCarousel(api, {
      prev: root.querySelector('[data-gx-image-slider-prev]'),
      next: root.querySelector('[data-gx-image-slider-next]'),
      current: root.querySelector('[data-gx-image-slider-current]')
    });
  });

    /* --- 7. App redirection (AppRedirection) ---------------------------------- */
  /* No carousel/scrub on .com; the CTA + AppDownload fade in via InView, which
     the shared [data-gx-reveal] observer already drives. This init only adds a
     graceful safety net: if the section is already on-screen at load (short
     pages, no scroll) and IntersectionObserver is unavailable, force-reveal it. */
  GX.inits.push(function () {
    var section = ROOT.querySelector('.app-redirection');
    if (!section) return;
    if (!('IntersectionObserver' in window)) {
      section.querySelectorAll('[data-gx-reveal]').forEach(function (el) {
        el.classList.add('is-inview');
      });
    }
  });


  /* --- 6. Awards slider (AwardSlider + AwardCard) ----------------------------
     The .com renders two DOM branches by viewport (isTablet = max-width:767px):
       - DESKTOP (>=768, gsap+Draggable): a paused timeline translates the
         medallion row (.award-slider__awards); a parallel scale timeline
         sinks/fades the passed cards. Draggable on the row seeks both live;
         arrows step current; the left content + pager reflect it. Item wrappers
         stagger-fade in on enter.
       - MOBILE (<768): a separate Embla container (.award-slider__carousel over
         .award-slider__list) carries the same medallions; arrows + pager wired
         via GX.wireCarousel.
     Degrades gracefully: with no Draggable the desktop row stays static and
     readable (CSS fallback shows wrappers); with no Embla the mobile list is a
     readable swipe-free flex row. -------------------------------------------- */
  GX.inits.push(function () {
    var root = ROOT.querySelector('[data-gx-award-slider]');
    if (!root) return;
    var awardsEl = root.querySelector('[data-gx-award-awards]');          /* desktop row */
    var viewport = root.querySelector('[data-gx-award-viewport]');        /* mobile embla viewport */
    var items = awardsEl
      ? Array.prototype.slice.call(awardsEl.querySelectorAll('.award-slider__item'))
      : [];
    var wrappers = awardsEl
      ? Array.prototype.slice.call(awardsEl.querySelectorAll('.award-slider__item-wrapper'))
      : [];
    var contents = Array.prototype.slice.call(
      root.querySelectorAll('[data-gx-award-content]')
    );
    var prevBtn = root.querySelector('[data-gx-award-prev]');
    var nextBtn = root.querySelector('[data-gx-award-next]');
    var currentEl = root.querySelector('[data-gx-award-current]');
    var total = contents.length;
    if (!total) return;

    var AWARDS_WIDTH = 264;
    var AWARDS_SPACE = 50;
    var PITCH = AWARDS_WIDTH + AWARDS_SPACE; /* 314 */
    var WIDTH_SLIDES = items.length * PITCH;

    var index = 0;
    var moving = false;
    var desktopReady = false;
    var revealed = false;
    var emblaApi = null;
    var tl = null;
    var tlScale = null;

    function reflectCurrent() {
      contents.forEach(function (c, i) { c.classList.toggle('animate-in', i === index); });
      if (currentEl) currentEl.textContent = index + 1;
      if (prevBtn) prevBtn.disabled = index === 0;
      if (nextBtn) nextBtn.disabled = index === total - 1;
    }

    function progressFor(i) { return total > 1 ? i / (total - 1) : 0; }
    function seek(progress) {
      if (tl) tl.progress(progress);
      if (tlScale) tlScale.progress(progress);
    }

    /* stagger-fade the desktop medallions into view once the row enters */
    function revealItems() {
      if (revealed || !wrappers.length) return;
      revealed = true;
      if (GX.gsapOK && window.gsap) {
        window.gsap.fromTo(
          wrappers,
          { autoAlpha: 0, x: 40 },
          { autoAlpha: 1, x: 0, duration: 0.8, ease: 'power2.out', stagger: { each: 0.3 } }
        );
      } else {
        wrappers.forEach(function (w) { w.style.opacity = '1'; w.style.visibility = 'visible'; });
      }
    }
    function observeReveal() {
      if (!awardsEl) return;
      if (!('IntersectionObserver' in window)) { revealItems(); return; }
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { if (e.isIntersecting) { revealItems(); io.disconnect(); } });
      }, { threshold: 0.2 });
      io.observe(awardsEl);
    }

    function buildTimelines() {
      tl = window.gsap.timeline({ defaults: { ease: 'none' } }).pause();
      items.forEach(function (item, i) {
        tl.fromTo(item, { x: 0 }, { x: -PITCH * i, duration: i }, '<');
      });
      tlScale = window.gsap.timeline({ defaults: { ease: 'none' } }).pause();
      items.slice(0, items.length - 1).forEach(function (item) {
        tlScale.fromTo(
          item,
          { y: 0, scale: 1, opacity: 1 },
          { y: 20, scale: 0.94, opacity: 0, duration: 1 },
          '>'
        );
      });
    }

    function setupDesktop() {
      if (desktopReady || !awardsEl || !window.Draggable) return;
      root.classList.add('is-gx-animated');
      buildTimelines();
      revealItems();

      var proxy = document.createElement('div');
      var startX = 0;
      var startProgress = 0;
      var drag = new window.Draggable(proxy, {
        trigger: awardsEl,
        type: 'x',
        onPress: function () {
          window.gsap.killTweensOf(tl);
          window.gsap.killTweensOf(tlScale);
          startX = this.x;
          startProgress = tl.progress();
        },
        onDrag: function () {
          var change = (startX - this.x) / WIDTH_SLIDES;
          seek(Math.max(0, Math.min(1, startProgress + change)));
        },
        onRelease: function () {
          index = Math.max(0, Math.min(total - 1, Math.round(tl.progress() * (total - 1))));
          seek(progressFor(index));
          reflectCurrent();
        }
      });
      drag.enable();
      desktopReady = true;
      seek(progressFor(index));
    }

    function setupMobile() {
      if (emblaApi || !viewport) return;
      emblaApi = GX.embla(viewport, { containScroll: false, slidesToScroll: 1 });
      if (!emblaApi) return; /* no Embla: list degrades to a swipe-free flex row */
      GX.wireCarousel(emblaApi, { prev: prevBtn, next: nextBtn, current: currentEl });
      emblaApi.on('select', function () { index = emblaApi.selectedScrollSnap(); });
    }

    function stepDesktop(delta) {
      if (moving) return;
      var next = Math.max(0, Math.min(total - 1, index + delta));
      if (next === index) return;
      index = next;
      moving = true;
      seek(progressFor(index));
      reflectCurrent();
      if (window.gsap) { window.gsap.delayedCall(0.3, function () { moving = false; }); }
      else { moving = false; }
    }

    /* On desktop the arrows step the draggable timeline; on mobile Embla wires
       its own click handlers via GX.wireCarousel, so only bind desktop here. */
    function onPrev() { if (desktopReady) stepDesktop(-1); }
    function onNext() { if (desktopReady) stepDesktop(1); }
    if (prevBtn) prevBtn.addEventListener('click', onPrev);
    if (nextBtn) nextBtn.addEventListener('click', onNext);

    function mountForViewport() {
      var desktop = window.matchMedia('(min-width: 768px)').matches;
      if (desktop) {
        /* The mobile carousel container is display:none here, so its Embla
           instance is inert; leave it untouched and bring up the draggable. */
        if (GX.gsapOK && window.Draggable) {
          setupDesktop();
          reflectCurrent();
        }
      } else {
        setupMobile();
      }
    }

    reflectCurrent();
    observeReveal();
    mountForViewport();
    window.addEventListener('resize', function () {
      mountForViewport();
      if (desktopReady) seek(progressFor(index));
    });
  });

/* @@INITS@@ */

  function boot() {
    GX.initReveal();
    GX.inits.forEach(function (fn) {
      try { fn(); } catch (e) { if (window.console) console.warn('[about] init failed', e); }
    });
    if (GX.gsapOK && window.ScrollTrigger) window.ScrollTrigger.refresh();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
